import React, { useState } from "react";
import { FileDown, Loader2, Mail, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Btn, Panel } from "@/components/portal/crm/kit";
import { day, money } from "@/lib/companyCheck";
import "@/styles/company-check.css";

const Tile = ({ k, v, s, tone, gauge, foot, testId }) => (
  <div className={`cc-tile${tone ? ` t-${tone}` : ""}`} data-testid={testId}>
    <div className="cc-tile-k">{k}</div>
    <div className="cc-tile-v">{v}</div>
    {gauge != null && <div className="cc-gauge" style={{ color: { green: "#0B6B4F", amber: "#E39A00", red: "#C8151F" }[tone] || "#0055FF" }}><i style={{ width: `${gauge}%` }} /></div>}
    {s && <div className="cc-tile-s">{s}</div>}
    {foot}
  </div>
);

const old = (iso) => iso && (Date.now() - new Date(iso).getTime()) / 864e5 > 548;

const turnoverSub = (f0, said) => {
  if (!f0) return "No accounts filed";
  if (f0.turnover != null) return old(f0.period) ? `${day(f0.period)} · out of date` : `Accounts to ${day(f0.period)}`;
  if (said) return `Customer stated ${money(said)}`;
  return f0.pl_filed === false ? "Small-company accounts · no P&L filed" : "Not in filed accounts";
};

export const Flags = ({ flags, max, testId = "cc-flags" }) => (
  <ul className="cc-flags" data-testid={testId}>
    {(flags || []).slice(0, max || undefined).map((f, i) => (
      <li key={i} className={`cc-flag f-${f.tone}`} data-testid={`${testId}-${i}`}>{f.text}</li>
    ))}
  </ul>
);

/** The customer's own figures, read by Connie — the gap the public record leaves, clearly labelled. */
function DocsTile({ docs }) {
  const t = docs?.turnover;
  const m = docs?.money_in;
  const n = docs?.items?.length || 0;
  if (t || m) {
    return <Tile k="Documents" testId="cc-tile-docs" tone="blue" v={t ? money(t.value) : `${money(m.value)}/mo`}
                 s={t ? `Turnover${t.period_end ? ` to ${day(t.period_end)}` : ""} · read by Connie` : "Paid in a month · read by Connie"} />;
  }
  if (docs?.reading) {
    return <Tile k="Documents" testId="cc-tile-docs" v={<span className="cc-reading"><Loader2 className="size-4 animate-spin" />Reading</span>}
                 s={`Connie has ${docs.reading} to read`} />;
  }
  return <Tile k="Documents" testId="cc-tile-docs" v={n ? String(n) : "—"} s={n ? "Read by Connie" : "None from the customer yet"} />;
}

function Tiles({ d }) {
  const r = d.report;
  const f0 = (r.financials || [])[0];
  const ccj = r.ccj;
  const age = r.company?.age_years;
  const said = Number(d.stated?.turnover) || null;
  const hl = d.highlights || [];
  return (
    <div className="cc-tiles">
      <Tile k="Highlights" testId="cc-tile-highlights" tone={hl.length ? "green" : undefined}
            v={hl.length ? String(hl.length) : "—"} s={hl.length ? `Plus point${hl.length === 1 ? "" : "s"}` : "Nothing on record yet"} />
      <DocsTile docs={d.docs} />
      <Tile k="CCJs" testId="cc-tile-ccj" tone={ccj ? (ccj.unsatisfied ? "red" : ccj.total ? "amber" : "green") : undefined}
            v={ccj ? (ccj.unsatisfied ? `${ccj.unsatisfied} open` : String(ccj.total)) : "—"}
            s={ccj ? (ccj.unsatisfied ? `${money(ccj.unsatisfied_value)} unsatisfied` : ccj.total ? "All satisfied" : "None in 6 years") : "Not available"} />
      <Tile k="Trading" testId="cc-tile-age" tone={age != null && age < 2 ? "amber" : undefined}
            v={age == null ? "—" : age < 1 ? "Under 1 yr" : `${Math.floor(age)} yr${Math.floor(age) === 1 ? "" : "s"}`} s={r.company?.incorporated ? `Since ${day(r.company.incorporated)}` : ""} />
      <Tile k="Turnover" testId="cc-tile-turnover" v={f0 ? (f0.turnover != null ? money(f0.turnover) : "Not filed") : "—"}
            s={turnoverSub(f0, said)} />
    </div>
  );
}

function AdminRun({ cc, testId }) {
  const [num, setNum] = useState("");
  const [ask, setAsk] = useState(false);
  const needsNumber = !cc.data?.company_number;
  const go = async () => {
    setAsk(false);
    try {
      const r = await cc.run(needsNumber ? num.trim() : undefined);
      if (r?.run?.status === "queued") toast("Queued — it runs by itself in a few minutes");
      else if (r?.run?.status === "ready") toast.success("Company check refreshed");
      else if (r?.run?.reason) toast(r.run.reason);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't run the company check"); }
  };
  return (
    <div className="cc-run">
      {needsNumber && <input value={num} onChange={(e) => setNum(e.target.value)} placeholder="Companies House #" data-testid={`${testId}-number`} />}
      <Btn kind={needsNumber ? "primary" : "ghost"} sm onClick={() => (needsNumber ? go() : setAsk(true))}
           disabled={cc.busy || (needsNumber && num.trim().length < 6)} testId={`${testId}-run`}>
        {cc.busy ? <Loader2 className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}{needsNumber ? "Run company check" : "Refresh check"}
      </Btn>
      <AlertDialog open={ask} onOpenChange={setAsk}>
        <AlertDialogContent data-testid={`${testId}-refresh-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Pull a fresh company check?</AlertDialogTitle>
            <AlertDialogDescription>
              It re-reads every part of the company check live, so it uses roughly 100 company-data credits.
              The desks see the new figures straight away.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testId}-refresh-cancel`}>Not now</AlertDialogCancel>
            <AlertDialogAction onClick={go} data-testid={`${testId}-refresh-confirm`}>Refresh check</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** The whole check as a branded Connexis PDF, stamped with who it was made for — hidden when the desk has it off. */
export function PdfButton({ cc, testId }) {
  const [busy, setBusy] = useState(false);
  if (!cc.data?.pdf) return null;
  const go = async () => {
    setBusy(true);
    try {
      await cc.pdf();
      toast.success("Company check PDF downloaded");
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "PDF downloads are switched off for now" : "Couldn't make the PDF just now — try again in a moment");
    } finally { setBusy(false); }
  };
  return (
    <Btn kind="ghost" sm onClick={go} disabled={busy} testId={testId}>
      {busy ? <Loader2 className="size-3.5 animate-spin" /> : <FileDown className="size-3.5" />}
      {busy ? "Making the PDF…" : "Download PDF"}
    </Btn>
  );
}

/** The headline on the case page: five numbers, the findings, and the way into the full report. */
export function CompanyCheckCard({ cc, role, onOpen, onEmail, testId = "company-check" }) {
  const d = cc.data;
  if (!d) return null;
  const admin = role === "admin";
  const r = d.report;
  const checks = (r?.flags || []).filter((f) => f.tone !== "green").length;
  if (d.status === "none" || d.status === "skipped") {
    if (!admin) return null;
    const sub = d.enabled === false ? "Company checks aren't switched on."
      : d.paused ? "Connexis search is switched off in Desk tools — switch it on to run a check on this case."
      : "No company check on this case yet — it runs by itself on cases submitted with a Companies House number.";
    return (
      <Panel title="Company check" sub={sub} testId={testId}>
        {d.enabled !== false && !d.paused && <AdminRun cc={cc} testId={testId} />}
      </Panel>
    );
  }
  if (d.status !== "ready" || !r) {
    const waiting = ["running", "queued"].includes(d.status);
    return (
      <Panel title="Company check" testId={testId}>
        <div className="cc-wait" data-testid={`${testId}-state`}>
          {waiting && <Loader2 className="size-4 animate-spin" />}
          <span>{waiting ? (d.reason || "The company check is being pulled — it lands here in a moment.") : (d.reason || "The company check didn't complete.")}</span>
        </div>
        {admin && !waiting && <AdminRun cc={cc} testId={testId} />}
      </Panel>
    );
  }
  const c = r.company || {};
  const active = (c.status || "").toLowerCase() === "active";
  return (
    <section className="card cc" data-testid={testId}>
      <div className="card-b">
        <div className="cc-h">
          <div style={{ minWidth: 0 }}>
            <div className="cc-eyebrow" data-testid={`${testId}-eyebrow`}><i />Company Check – Connexis</div>
            <div className="cc-name" data-testid={`${testId}-name`}>
              {c.name || "The company"}
              {c.status && <span className={`cc-status ${active ? "is-ok" : "is-bad"}`} data-testid={`${testId}-status`}>{c.status}</span>}
            </div>
            <div className="cc-meta">
              <b>{c.number}</b>{c.type ? ` · ${c.type}` : ""}{c.size ? ` · ${c.size}` : ""}{c.vat_number ? ` · VAT ${c.vat_number}` : ""}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <PdfButton cc={cc} testId={`${testId}-pdf`} />
            {onEmail && d.pdf && (
              <Btn kind="ghost" sm onClick={onEmail} testId={`${testId}-email`}><Mail className="size-3.5" /> {d.broker ? "Email to your lenders" : "Email to credit"}</Btn>
            )}
            <Btn kind="ink" sm onClick={onOpen} testId={`${testId}-open`}>Open full report</Btn>
          </div>
        </div>
        <Tiles d={d} />
        <Flags flags={d.highlights} max={4} testId={`${testId}-flags`} />
        {checks > 0 && (
          <button type="button" className="cc-more" onClick={onOpen} data-testid={`${testId}-checks`}>
            {checks} thing{checks === 1 ? "" : "s"} to check in the full report →
          </button>
        )}
        <div className="cc-foot">
          <span className="cc-src" data-testid={`${testId}-checked`}>
            Checked by Connexis {day(d.checked_at)}
            {admin && (
              <span className="cc-internal" data-testid={`${testId}-internal`}>
                {d.credits_used ? ` · ${d.credits_used} credit${d.credits_used === 1 ? "" : "s"} used` : ""}
                {d.account?.credits_remaining != null ? ` · ${d.account.credits_remaining} left` : ""}
                {" "}· Credits are Connexis only — the desks and the introducer see the full check, never the credits.
              </span>
            )}
          </span>
          {admin && <AdminRun cc={cc} testId={testId} />}
        </div>
      </div>
    </section>
  );
}

export default CompanyCheckCard;
